import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    Grid,
    CircularProgress
} from "@mui/material";
import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import WSTextField, { CurrencyType, TextType } from "../../Components/TextBox/WSTextBox";
import { useForm } from "react-hook-form";
import WSDateField from "../../Components/DateField/WSDateField";
import { HeaderList, IHeaderListActions } from "../../Components/Header/HeaderList";
import { WSIcons } from "../../commonFunctions/iIcons";
import dayjs from "dayjs";
import WSSelectField from "../../Components/Select/WSSelect";
import config from '../../Config/clientConfig.json';

type EmiItem = {
    month: number;
    emi: number;
    interest: number;
    principal: number;
    balance: number;
    cumulativePrincipal: number;
    cumulativeInterest: number;
};

export default function CreateRepayment() {
    const { loanId } = useParams();
    const { showMessage } = useSnackbar();
    const navigate = useNavigate();

    const [row, setRow] = useState<any>();

    const getEndOfCurrentMonth = () => {
        const today = new Date();

        return dayjs(new Date(today.getFullYear(), today.getMonth() + 2, 0)).format("MM/DD/YYYY")
    };

    const { handleSubmit, setValue, control, watch } = useForm({
        defaultValues: {
            repaymentDate: getEndOfCurrentMonth(),
            principal: 0,
            interestRate: 0,
        }
    });

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [emiData, setEmiData] = useState<EmiItem[]>([]);
    const [loanData, setLoanData] = useState<any>(null);

    useEffect(() => {
        const fetchDataById = async () => {
            setLoading(true);
            try {
                const response: any = await callAPI(
                    "loans/getBy",
                    Method.GET,
                    {},
                    loanId
                );



                setRow(response.loan)

                if (response.success) {
                    setLoanData(response.loan);
                    setValue("principal", response.loan.principal || 0);
                    setValue("interestRate", response.loan.interestRate || 0);
                }

            } catch (error: any) {
                console.error("Failed to retrieve data", error);
                if (error.message === 'GlobalHandledError') return;
                showMessage(
                    error?.response?.data?.message || "Failed to retrieve data",
                    "error"
                );
            } finally {
                setLoading(false);
            }
        };
        if (loanId) {
            fetchDataById();
        }
    }, [loanId]);


    useEffect(() => {
        const fetchEmiSchedule = async () => {
            try {
                const res = await callAPI(`loans/emi-schedule/${loanData._id}`, Method.GET);
                setEmiData(res.schedule || []);
            } catch (err) {
                console.error("Failed to fetch EMI schedule", err);
            }
        };

        if (loanData?.status === 'disbursed') {
            fetchEmiSchedule();
        }
    }, [loanData]);

    const getEmiMonthIndex = (repaymentDateStr: string, fundTransferDateStr: string) => {
        const repaymentDate = new Date(repaymentDateStr);
        const fundTransferDate = new Date(fundTransferDateStr);

        // First EMI is at end of *next* month from fund transfer
        const firstEmiDue = new Date(fundTransferDate.getFullYear(), fundTransferDate.getMonth() + 1, 1);
        firstEmiDue.setMonth(firstEmiDue.getMonth() + 1);
        firstEmiDue.setDate(0); // Last day of that month

        // Now calculate how many EMIs passed between firstEmiDue and repaymentDate
        const monthsBetween =
            (repaymentDate.getFullYear() - firstEmiDue.getFullYear()) * 12 +
            (repaymentDate.getMonth() - firstEmiDue.getMonth());

        return Math.max(0, Math.min(monthsBetween, emiData.length - 1));
    };


    const repaymentDate = watch("repaymentDate");

    useEffect(() => {
        if (!repaymentDate || !emiData.length || !loanData?.fundTransferDate) return;

        const monthIndex = getEmiMonthIndex(repaymentDate, loanData.fundTransferDate);
        const currentEmi = emiData[monthIndex];

        if (currentEmi) {
            setValue("principal", currentEmi.principal);
            setValue("interestRate", currentEmi.interest);
        }
    }, [repaymentDate, emiData, loanData]);


    const handleData = async (data: any) => {
        setSubmitting(true);
        try {
            const request = {
                loan: loanId,
                amount: Number(data.amount), // Total amount paid
                principal: Number(data.principal || 0),
                interestRate: Number(data.interestRate || 0),
                repaymentDate: new Date(data.repaymentDate),
                receiptNumber: data.receiptNumber,
                remarks: data.remarks || "",
                paymentType: data.paymentType || "",
            };

            const response: any = await callAPI("repayments/create", Method.POST, request);

            if (response.success) {
                showMessage(response.message, "success");
                navigate(`/loan/detail/${loanId}`)
            }
        } catch (error: any) {
            if (error.message === 'GlobalHandledError') return;
            showMessage(
                error.response?.data.message || "An unexpected error occurred",
                "error"
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelClick = () => {
        navigate("/loan/requests/final/list")
    }


    const actions: IHeaderListActions[] = [
        {
            type: "button",
            text: "Cancel",
            size: "small",
            onClick: handleCancelClick,
            variant: "outlined",
            color: "error",
            icon: <WSIcons.CloseIcon />,
            disabled: submitting,
        },
        {
            type: "button",
            text: "Save",
            size: "small",
            buttonType: "submit",
            variant: "outlined",
            color: "success",
            icon: <WSIcons.Save />,
            loading: submitting,
        }
    ];

    if (loading)
        return (
            <Box height="80vh" display="flex" alignItems="center" justifyContent="center">
                <CircularProgress sx={{ color: "#4AD06E" }} />
            </Box>
        );

    if (loanData?.status !== 'disbursed') {
        return (<Grid sx={{
            padding: 3,
            backgroundColor: "#fff",
            borderBottomLeftRadius: 5,
            borderBottomRightRadius: 5,
            border: "1px solid lightgrey"
        }}>
            <Grid container spacing={2} mt={1}><Grid item xs={12}>
                <Typography variant="h6" fontWeight="bold" color="crimson">
                    Only Disbursed Loans can be repayable.
                </Typography>
            </Grid>
            </Grid>
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backdropFilter: 'blur(2px)',
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    zIndex: 1,
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 2,
                }}
            >
                <Typography variant="h6" sx={{ mb: 2, color: 'black' }}>
                    Only Disbursed loans can be repayable;
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => window.history.back()}
                    sx={{ zIndex: 2 }}
                >
                    Back
                </Button>
            </Box>
        </Grid>)
    }

    return (
        <Paper elevation={1}>
            <form onSubmit={handleSubmit(handleData)}>
                <HeaderList title="Add Repayment" type="card" actions={actions} isBackButton />

                {/* --- LOAN SUMMARY SECTION --- */}
                <Grid
                    item
                    container
                    spacing={1}
                    justifyContent={"start"}
                    sx={{
                        marginTop: 0,
                        marginLeft: 0,
                        width: "100%",
                        backgroundColor: "#e8f5e9", // Light green background for distinction
                        borderTopLeftRadius: 5,
                        borderTopRightRadius: 5,
                        border: "1px solid #c8e6c9",
                        padding: 2,
                    }}
                >
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 'bold', color: "#2e7d32", mb: 1 }}>
                            Loan Summary
                        </Typography>
                    </Grid>

                    {/* Loan ID */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Loan ID:</Typography>
                        <Typography variant="body2">{row?.loanId || 'N/A'}</Typography>
                    </Grid>

                    {/* Applicant Name */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Applicant Name:</Typography>
                        <Typography variant="body2">{row?.applicantName || 'N/A'}</Typography>
                    </Grid>

                    {/* Enterprise Name */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Enterprise:</Typography>
                        <Typography variant="body2">{row?.enterpriseName || 'N/A'}</Typography>
                    </Grid>

                    {/* NHG Name */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>NHG:</Typography>
                        <Typography variant="body2">{row?.nhgName || 'N/A'}</Typography>
                    </Grid>

                    {/* Sanctioned Amount */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Sanctioned Amount:</Typography>
                        <Typography variant="body2">
                            {row?.sanctionedCefAmount ? `₹${row.sanctionedCefAmount.toLocaleString('en-IN')}` : 'N/A'}
                        </Typography>
                    </Grid>

                    {/* Disbursed Amount */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Disbursed Amount:</Typography>
                        <Typography variant="body2">
                            {row?.amountReceived ? `₹${row.amountReceived.toLocaleString('en-IN')}` : 'N/A'}
                        </Typography>
                    </Grid>

                    {/* Interest Rate */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Interest Rate:</Typography>
                        <Typography variant="body2">
                            {row?.interestRate ? `${row.interestRate}%` : 'N/A'}
                        </Typography>
                    </Grid>

                    {/* Loan type group or not */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Loan Type:</Typography>
                        <Typography variant="body2" sx={{ color: 'error.main' }}>
                            {row?.isGroupLoan ? 'Group Loan' : 'Individual Loan'}
                        </Typography>
                    </Grid>
                </Grid>
                {/* --- END LOAN SUMMARY SECTION --- */}

                {/* Repayment form */}

                <Grid
                    item
                    container
                    spacing={1}
                    justifyContent={"start"}
                    sx={{
                        marginTop: 5,
                        marginLeft: 0,
                        width: "100%",
                        background: `linear-gradient(180deg, #4AD16E 0%, #08B1B6 100%)`,
                        color: "#fff",
                        borderBottom: '2px solid transparent',
                        borderImage: `linear-gradient(90deg, #08B1B6 0%, #4AD16E 100%) 1`,
                        borderTopLeftRadius: 5,
                        borderTopRightRadius: 5
                    }}
                >
                    <Grid
                        alignContent={"center"}
                        style={{ paddingLeft: 20, paddingTop: 0, height: "3rem" }}
                        item
                        sm={4}
                        className="ws-data-grid-lightviolet-title"
                    >
                        <Typography variant="h6" id="tableTitle" sx={{ fontSize: "1rem" }}>
                            Repayment Information
                        </Typography>
                    </Grid>
                </Grid>

                {/* Form Body */}
                <Grid
                    sx={{
                        padding: 3,
                        backgroundColor: "#fff",
                        borderBottomLeftRadius: 5,
                        borderBottomRightRadius: 5,
                        border: "1px solid lightgrey"
                    }}
                >
                    <Grid container spacing={2}>
                        {/* Repayment Date */}
                        <Grid item xs={12} sm={6}>
                            <WSDateField
                                id="repaymentDate"
                                label="Repayment Date"
                                name="repaymentDate"
                                control={control}
                                rules={{ required: true }}
                            />
                        </Grid>

                        {/* Receipt Number */}
                        <Grid item xs={12} sm={6}>
                            <WSTextField
                                id="receiptNumber"
                                label="Receipt Number"
                                name="receiptNumber"
                                control={control}
                                rules={{ required: true }}
                            />
                        </Grid>

                        {/* Amount */}
                        <Grid item xs={12} sm={6}>
                            <WSTextField
                                id="amount"
                                label="Amount"
                                name="amount"
                                type={TextType.Currency}
                                currency={CurrencyType.Rupee}
                                control={control}
                                rules={{ required: true }}
                            />
                        </Grid>

                        {/* paymentType */}
                        <Grid item xs={12} sm={6}>
                            <WSSelectField
                                id="paymentType"
                                label="Payment Type"
                                name="paymentType"
                                options={config.paymentMethodConfig.staticOptionData}
                                defaultValue={config.paymentMethodConfig.staticOptionData[1].value}
                                control={control}
                                rules={{ required: true }}
                            />
                        </Grid>

                        {/* Principal Component */}
                        {/* <Grid item xs={12} sm={6}>
                            <WSTextField
                                id="principal"
                                label="Principal Component"
                                name="principal"
                                type={TextType.Currency}
                                currency={CurrencyType.Rupee}
                                control={control}
                            // disabled
                            />
                        </Grid> */}

                        {/* Interest Component */}
                        {/* <Grid item xs={12} sm={6}>
                            <WSTextField
                                id="interestRate"
                                label="Interest Component"
                                name="interestRate"
                                type={TextType.Currency}
                                currency={CurrencyType.Rupee}
                                control={control}
                            // disabled
                            />
                        </Grid> */}

                        {/* Remarks */}
                        <Grid item xs={12} sm={12}>
                            <WSTextField
                                id="remarks"
                                label="Remarks"
                                name="remarks"
                                control={control}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </form>
        </Paper>
    );
}